import type { EmbedDomain } from '../api/endpoints';

/**
 * Список доменов встраивания (FR-635, FR-636): приведение и проверка записи.
 *
 * Правила повторяют `normaliseOrigin` / `validateOrigin` сервера
 * (`apps/api/src/embed/embed-csp.ts`) — форма, пропускающая то, что сервер отклонит,
 * превращает каждую опечатку в общий отказ `embed_origin_invalid` без указания строки.
 * Сервер проверяет то же самое ещё раз: клиентская проверка — подсказка, а не защита.
 */

export const ORIGIN_MAX = 255;
export const COMMENT_MAX = 255;
/** Предел схемы `PUT /web/embed-domains` (`maxItems`). */
export const DOMAINS_MAX = 50;

/** Код ошибки строки — ключ в `messages.cabinet.embed.errors`. Совпадает с кодом сервера. */
export type OriginViolation =
  | 'origin_required'
  | 'origin_malformed'
  | 'origin_scheme_not_allowed'
  | 'origin_wildcard_too_broad'
  | 'origin_path_not_allowed'
  | 'origin_duplicate'
  | 'origin_too_long'
  | 'comment_too_long';

export interface DomainRow {
  readonly origin: string;
  readonly comment: string;
}

const ORIGIN_PATTERN = /^(https?):\/\/(\*\.)?([a-z0-9-]+(\.[a-z0-9-]+)*)(:[0-9]{1,5})?$/;
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
const SCHEME_SEPARATOR = '://';
/** `*.example.uz` — допустимо, `*.uz` — нет: под звёздочкой минимум домен второго уровня. */
const MIN_WILDCARD_LABELS = 2;

/** `HTTPS://CT.Example.UZ/panel/` → `https://ct.example.uz`: один источник — одна запись. */
export function normaliseOrigin(value: string): string {
  const trimmed = value.trim().replace(/\/+$/, '');
  const separator = trimmed.indexOf(SCHEME_SEPARATOR);

  if (separator < 0) {
    return trimmed.toLowerCase();
  }

  const scheme = trimmed.slice(0, separator).toLowerCase();
  const host = (
    trimmed.slice(separator + SCHEME_SEPARATOR.length).split('/')[0] ?? ''
  ).toLowerCase();

  return `${scheme}${SCHEME_SEPARATOR}${host}`;
}

/**
 * Проверка источника. В отличие от сервера, путь в записи здесь — ошибка, а не молча
 * отрезаемый хвост: человек, вставивший `https://ct.uz/panel`, должен увидеть, что
 * встраивание разрешается сайту целиком, а не странице.
 */
export function validateOrigin(value: string): OriginViolation | null {
  const trimmed = value.trim().replace(/\/+$/, '');

  if (trimmed === '') {
    return 'origin_required';
  }

  if (trimmed.length > ORIGIN_MAX) {
    return 'origin_too_long';
  }

  const separator = trimmed.indexOf(SCHEME_SEPARATOR);

  if (separator >= 0 && trimmed.includes('/', separator + SCHEME_SEPARATOR.length)) {
    return 'origin_path_not_allowed';
  }

  const origin = normaliseOrigin(trimmed);
  const match = ORIGIN_PATTERN.exec(origin);

  if (match === null) {
    return origin.startsWith('http://') || origin.startsWith('https://')
      ? 'origin_malformed'
      : 'origin_scheme_not_allowed';
  }

  const scheme = match[1] ?? '';
  const wildcard = match[2] !== undefined;
  const host = match[3] ?? '';

  // `http` — только для стенда: в бою cookie с `Secure` по такому адресу не уйдёт.
  if (scheme === 'http' && !LOCAL_HOSTS.includes(host)) {
    return 'origin_scheme_not_allowed';
  }

  // `*.uz` открыл бы встраивание любому сайту в зоне.
  if (wildcard && host.split('.').length < MIN_WILDCARD_LABELS) {
    return 'origin_wildcard_too_broad';
  }

  return null;
}

/** Ошибки по строкам (индекс строки → код). Пустой объект — список можно сохранять. */
export function validateDomainRows(
  rows: readonly DomainRow[],
): Readonly<Record<number, OriginViolation>> {
  const errors: Record<number, OriginViolation> = {};
  const seen = new Map<string, number>();

  rows.forEach((row, index) => {
    const violation = validateOrigin(row.origin);

    if (violation !== null) {
      errors[index] = violation;

      return;
    }

    if (row.comment.trim().length > COMMENT_MAX) {
      errors[index] = 'comment_too_long';

      return;
    }

    const origin = normaliseOrigin(row.origin);

    if (seen.has(origin)) {
      errors[index] = 'origin_duplicate';
    } else {
      seen.set(origin, index);
    }
  });

  return errors;
}

export function domainRowsFrom(domains: readonly EmbedDomain[]): DomainRow[] {
  return domains.map((domain) => ({ origin: domain.origin, comment: domain.comment ?? '' }));
}

/** Тело `PUT /web/embed-domains`: источники приведены, пустой комментарий — `null`. */
export function toEmbedDomains(rows: readonly DomainRow[]): EmbedDomain[] {
  return rows.map((row) => {
    const comment = row.comment.trim();

    return { origin: normaliseOrigin(row.origin), comment: comment === '' ? null : comment };
  });
}

/**
 * Ошибки сервера по строкам. Сервер называет поле `origin` без индекса, но кладёт
 * исходное значение в `message` — по нему строка и находится.
 */
export function rowErrorsFromProblem(
  rows: readonly DomainRow[],
  errors: readonly { readonly field: string; readonly code: string; readonly message?: string }[],
): Record<number, OriginViolation> {
  const result: Record<number, OriginViolation> = {};

  for (const error of errors) {
    const index = rows.findIndex(
      (row) => row.origin === error.message || normaliseOrigin(row.origin) === error.message,
    );

    if (index >= 0 && isViolation(error.code)) {
      result[index] = error.code;
    }
  }

  return result;
}

function isViolation(code: string): code is OriginViolation {
  return [
    'origin_malformed',
    'origin_scheme_not_allowed',
    'origin_wildcard_too_broad',
    'origin_path_not_allowed',
  ].includes(code);
}

export function sameDomains(left: readonly DomainRow[], right: readonly DomainRow[]): boolean {
  const a = JSON.stringify(toEmbedDomains(left));
  const b = JSON.stringify(toEmbedDomains(right));

  return a === b;
}
